import React, { useState } from 'react'
import { getInitials } from '@/utils/formatters'
import { cn } from '@/utils/cn'

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface AvatarProps {
  name?: string | null
  src?: string | null
  size?: AvatarSize
  className?: string
  onClick?: () => void
}

const sizeConfig: Record<AvatarSize, { container: string; text: string; fontSize: string }> = {
  xs: { container: 'w-6 h-6', text: 'text-[10px]', fontSize: '10px' },
  sm: { container: 'w-8 h-8', text: 'text-xs', fontSize: '12px' },
  md: { container: 'w-9 h-9', text: 'text-sm', fontSize: '13px' },
  lg: { container: 'w-11 h-11', text: 'text-base', fontSize: '15px' },
  xl: { container: 'w-16 h-16', text: 'text-xl', fontSize: '20px' },
}

// Generate a consistent color from a string
function getAvatarColor(name: string | null | undefined): string {
  if (!name) return '#A0AABF'
  const colors = [
    '#1A6AB4', '#3DC7B3', '#7C3AED', '#2DB37A',
    '#E11D48', '#4F46E5', '#F59E0B', '#EF4444',
    '#06B6D4', '#8B5CF6', '#10B981', '#F97316',
  ]
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}

export function Avatar({ name, src, size = 'md', className, onClick }: AvatarProps) {
  const [imgError, setImgError] = useState(false)
  const config = sizeConfig[size]
  const initials = getInitials(name)
  const bgColor = getAvatarColor(name)

  const showImage = src && !imgError

  return (
    <div
      className={cn(
        config.container,
        'relative rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center select-none',
        onClick && 'cursor-pointer',
        className
      )}
      style={!showImage ? { backgroundColor: bgColor } : undefined}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      {showImage ? (
        <img
          src={src}
          alt={name || 'Avatar'}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <span
          className={cn('font-semibold text-white leading-none', config.text)}
          style={{ fontSize: config.fontSize }}
          aria-label={name || undefined}
        >
          {initials}
        </span>
      )}
    </div>
  )
}

// Avatar group - shows stacked avatars with overflow count
interface AvatarGroupProps {
  items: Array<{ name?: string; src?: string; id: string }>
  max?: number
  size?: AvatarSize
  className?: string
}

export function AvatarGroup({ items, max = 3, size = 'sm', className }: AvatarGroupProps) {
  const visible = items.slice(0, max)
  const overflow = items.length - max

  return (
    <div className={cn('flex items-center -space-x-2', className)}>
      {visible.map((item) => (
        <div key={item.id} className="ring-2 ring-white dark:ring-slate-800 rounded-full">
          <Avatar name={item.name} src={item.src} size={size} />
        </div>
      ))}
      {overflow > 0 && (
        <div
          className={cn(
            sizeConfig[size].container,
            'rounded-full ring-2 ring-white dark:ring-slate-800 flex items-center justify-center bg-gray-200 dark:bg-slate-600'
          )}
        >
          <span className={cn('text-gray-600 dark:text-slate-300 font-medium', sizeConfig[size].text)}>
            +{overflow}
          </span>
        </div>
      )}
    </div>
  )
}

export default Avatar
